"use client"

import { useCallback, useEffect, useState } from "react"
import { X, CheckCheck } from "lucide-react"
import { useRouter } from "next/navigation"
import { useNotificationStore } from "@/store/notification-store"
import { authFetch } from "@/lib/auth-fetch"
import { PushNotification } from "@/store/notification-store"
import {
  SignalRow,
  SignalLogTab,
  FeedSignalTab,
  SignalLogEntry,
  FeedSignalEntry,
  extractSymbol,
} from "@/components/signals-widget"

export function SignalAlertsModal() {
  const router = useRouter()
  const { notifications, showSignalModal, setShowSignalModal, markRead, markAllRead } = useNotificationStore()

  const [activeTab, setActiveTab] = useState<"algo" | "feed" | "log">("algo")
  const algoNotifications = notifications.filter(n => n.tag !== "FEED")
  const algoUnread = algoNotifications.filter(n => !n.read).length

  const [feedEntries, setFeedEntries] = useState<FeedSignalEntry[]>([])
  const [feedLoading, setFeedLoading] = useState(true)
  const [logEntries, setLogEntries] = useState<SignalLogEntry[]>([])
  const [logLoading, setLogLoading] = useState(true)

  const fetchFeed = useCallback(() => {
    authFetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/exchange/feed-signal-log?limit=50`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setFeedEntries(data) })
      .catch(() => {})
      .finally(() => setFeedLoading(false))
  }, [])

  const fetchLog = useCallback(() => {
    authFetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/exchange/signal-log?limit=50`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setLogEntries(data) })
      .catch(() => {})
      .finally(() => setLogLoading(false))
  }, [])

  useEffect(() => {
    if (!showSignalModal) return
    fetchFeed()
    fetchLog()
    const feedInterval = setInterval(fetchFeed, 20_000)
    const logInterval = setInterval(fetchLog, 20_000)
    return () => {
      clearInterval(feedInterval)
      clearInterval(logInterval)
    }
  }, [showSignalModal, fetchFeed, fetchLog])

  const handleSignalClick = useCallback((n: PushNotification) => {
    markRead(n.id)
    const sym = extractSymbol(n)
    setShowSignalModal(false)
    router.push(sym ? `/terminal/futures/${sym}` : "/terminal")
  }, [markRead, setShowSignalModal, router])

  const handleLogNavigate = useCallback((symbol: string) => {
    setShowSignalModal(false)
    router.push(`/terminal/futures/${symbol}`)
  }, [setShowSignalModal, router])

  if (!showSignalModal) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={() => setShowSignalModal(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-xl" />

      {/* Sheet */}
      <div
        className="relative z-10 w-full max-w-md mx-0 sm:mx-4 bg-card border border-border shadow-2xl flex flex-col"
        style={{ maxHeight: 'min(600px, 85dvh)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center border-b border-border px-3 py-2 shrink-0">
          {/* Tabs */}
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => setActiveTab("algo")}
              className={`text-[11px] font-semibold transition-colors ${activeTab === "algo" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Algo
            </button>
            {algoUnread > 0 && activeTab === "algo" && (
              <span className="text-[9px] font-bold bg-primary text-primary-foreground rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 leading-none">
                {algoUnread}
              </span>
            )}
            <span className="text-muted-foreground text-[10px]">/</span>
            <button
              onClick={() => setActiveTab("feed")}
              className={`text-[11px] font-semibold transition-colors ${activeTab === "feed" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Feed
            </button>
            <span className="text-muted-foreground text-[10px]">/</span>
            <button
              onClick={() => setActiveTab("log")}
              className={`text-[11px] font-semibold transition-colors ${activeTab === "log" ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              Algo Log
            </button>
          </div>

          {activeTab === "algo" && algoUnread > 0 && (
            <button
              onClick={markAllRead}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="Mark all read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={() => setShowSignalModal(false)}
            className="flex items-center justify-center w-7 h-7 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {activeTab === "algo" ? (
            algoNotifications.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-[11px] text-muted-foreground">
                No signals yet
              </div>
            ) : (
              <div className="flex flex-col">
                {algoNotifications.map(n => (
                  <SignalRow
                    key={n.id}
                    notification={n}
                    onClick={() => handleSignalClick(n)}
                  />
                ))}
              </div>
            )
          ) : activeTab === "feed" ? (
            <FeedSignalTab
              onNavigate={handleLogNavigate}
              entries={feedEntries}
              loading={feedLoading}
            />
          ) : (
            <SignalLogTab
              onNavigate={handleLogNavigate}
              entries={logEntries}
              loading={logLoading}
            />
          )}
        </div>
      </div>
    </div>
  )
}
